import React, { useState } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { FieldGrid } from './FieldControl.jsx';

// A card of same-shaped items.
//
//   "+ Add" in the header opens one blank inline form.
//   Hovering an item reveals a pencil + a bin; one item edits at a time.
//   No section-level edit mode.
//
// Props:
//   items        array of records, each with an `id`
//   fields       FieldControl descriptors for the inline add / edit form
//   toForm(item) record -> working form object (also used to normalise
//                existing rows before a replace); called with {} for a new row
//   fromForm(f)  working form -> payload object (trim / nullify here)
//   keepIf(p)    optional: drop payloads that fail this test (blank rows)
//   onReplace(nextPayloads)  async; receives the whole new list
//   renderItem(item, { busy })  read-mode display for one item; `busy` is
//                      true while any list save (or an externalBusy op) is in
//                      flight, so an inline control can disable itself
//   externalBusy  optional: a mutation the parent runs against the same rows
//                      (e.g. a per-item PATCH). While true the Add / edit /
//                      remove controls are disabled, so the two can't race.
//   extraAction(item)  optional icon button shown before the pencil on hover
//                      (used for the resource "key document" star)
//   isPinned(item) + pinnedLabel  optional: items matching isPinned render
//                      first under a small label, then the rest
export function EditableList({
  title,
  subtitle,
  items,
  fields,
  toForm,
  fromForm,
  keepIf,
  onReplace,
  renderItem,
  externalBusy = false,
  extraAction,
  isPinned,
  pinnedLabel,
  addLabel = 'Add',
  emptyLabel = 'Nothing here yet.',
  className = '',
  // `bare` drops the .card chrome so the list can sit inside an .info-group
  // (used for the client health reasons).
  bare = false,
}) {
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isBusy = busy || externalBusy;

  const asPayload = (item) => fromForm(toForm(item));

  async function commit(nextPayloads) {
    setBusy(true);
    setError('');
    try {
      const filtered = keepIf ? nextPayloads.filter(keepIf) : nextPayloads;
      await onReplace(filtered);
      setAdding(false);
      setEditingId(null);
      setConfirmId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // `keepIf` also decides whether a form is complete: a blank required field
  // must be reported, not silently dropped (which the replace-all save would
  // turn into a delete of the row without the Remove confirmation).
  const incomplete = (form) => keepIf && !keepIf(fromForm(form));

  const addItem = (form) => {
    if (incomplete(form)) return setError('Fill in the required fields.');
    return commit([...items.map(asPayload), fromForm(form)]);
  };
  const updateItem = (id, form) => {
    if (incomplete(form)) return setError('Fill in the required fields.');
    return commit(
      items.map((it) => (it.id === id ? fromForm(form) : asPayload(it))),
    );
  };
  const removeItem = (id) =>
    commit(items.filter((it) => it.id !== id).map(asPayload));

  const Wrapper = bare ? 'div' : 'section';
  const Heading = bare ? 'h3' : 'h2';
  const base = bare
    ? 'info-group editable-list editable-list-bare'
    : 'card editable-list';

  return (
    <Wrapper className={className ? `${base} ${className}` : base}>
      <div className="editable-list-head">
        <div>
          {title ? <Heading>{title}</Heading> : null}
          {subtitle && <p className="section-subtitle">{subtitle}</p>}
        </div>
        <button
          type="button"
          className="secondary small"
          onClick={() => {
            setError('');
            setAdding(true);
            setEditingId(null);
          }}
          disabled={adding || isBusy}
        >
          <Plus size={14} /> {addLabel}
        </button>
      </div>

      {error && <div className="error">{error}</div>}

      {adding && (
        <ItemForm
          fields={fields}
          initial={toForm({})}
          busy={isBusy}
          onCancel={() => {
            setAdding(false);
            setError('');
          }}
          onSave={addItem}
        />
      )}

      {items.length === 0 && !adding ? (
        <p className="muted-text">{emptyLabel}</p>
      ) : (
        renderGroups()
      )}
    </Wrapper>
  );

  function renderGroups() {
    if (!isPinned)
      return <ul className="editable-list-items">{items.map(renderRow)}</ul>;
    const pinned = items.filter(isPinned);
    const rest = items.filter((item) => !isPinned(item));
    return (
      <>
        {pinned.length > 0 && (
          <>
            {pinnedLabel && (
              <div className="editable-list-group">{pinnedLabel}</div>
            )}
            <ul className="editable-list-items">{pinned.map(renderRow)}</ul>
          </>
        )}
        {rest.length > 0 && (
          <ul className="editable-list-items">{rest.map(renderRow)}</ul>
        )}
      </>
    );
  }

  function renderRow(item) {
    if (editingId === item.id) {
      return (
        <li className="editable-list-item is-editing" key={item.id}>
          <ItemForm
            fields={fields}
            initial={toForm(item)}
            busy={isBusy}
            onCancel={() => {
              setEditingId(null);
              setError('');
            }}
            onSave={(form) => updateItem(item.id, form)}
          />
        </li>
      );
    }
    return (
      <li className="editable-list-item" key={item.id}>
        <div className="editable-list-item-body">
          {renderItem(item, { busy: isBusy })}
        </div>
        {confirmId === item.id ? (
          <div className="editable-list-confirm">
            <span>Remove?</span>
            <button
              type="button"
              className="text-button danger-text"
              disabled={isBusy}
              onClick={() => removeItem(item.id)}
            >
              Remove
            </button>
            <button
              type="button"
              className="text-button"
              onClick={() => setConfirmId(null)}
            >
              Keep
            </button>
          </div>
        ) : (
          <div className="editable-list-item-actions">
            {extraAction && extraAction(item)}
            <button
              type="button"
              className="icon-button"
              aria-label="Edit"
              disabled={isBusy}
              onClick={() => {
                setEditingId(item.id);
                setAdding(false);
                setError('');
              }}
            >
              <Pencil size={15} />
            </button>
            <button
              type="button"
              className="icon-button"
              aria-label="Remove"
              disabled={isBusy}
              onClick={() => setConfirmId(item.id)}
            >
              <Trash2 size={15} />
            </button>
          </div>
        )}
      </li>
    );
  }
}

function ItemForm({ fields, initial, busy, onCancel, onSave }) {
  const [form, setForm] = useState(initial);
  return (
    <form
      className="inline-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSave(form);
      }}
    >
      <FieldGrid fields={fields} form={form} setForm={setForm} />
      <div className="inline-form-actions">
        <button
          type="button"
          className="text-button"
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </button>
        <button type="submit" className="primary small" disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}
